const negativeLines = Array.from({ length: 54 }, (v, i) => ((i+1) * -1).toString());
const positiveLines = Array.from({ length: 54 }, (v, i) => (i+1).toString()).reverse();
// "SIMPLE_-1" à "SIMPLE_-20"
const endZone = Array.from({ length: 20 }, (v, i) => 'SIMPLE_'+((i+1) * -1).toString());

export const SF = 'SF' //SAFETY
export const TD = 'TD'
export const SIMPLE = 'SIMPLE'
export const ENDZONE_DEF_INDEX = 0
export const ENDZONE_OFF_INDEX = 110

export const DEFAULT_FIRST_DOWN_DISTANCE = 10
export const DEFAULT_PAT_DISTANCE = 5
export const DEFAULT_YARD_LINE = '-35'
export const DEFAULT_YARD_LINE_TWENTY = '-20'
export const DEFAULT_KICKOFF_YARD_LINE = '-45'

/**
 * Liste canonique et IMMUABLE des positions sur le terrain (SF -> ... -> TD).
 * C'est la seule liste utilisée par les fonctions de calcul de jeu ci-dessous
 * (getYardIndex, getDistance, calculateYardLine).
 *
 * Historique (bug #429) : cette liste était auparavant une variable de module mutable
 * (`export let yardLines`), réassignée par selectProperWheelYardLine() à chaque écran de
 * saisie affiché (kickoff, punt, interception, etc.). Comme plusieurs roulettes pouvaient
 * être rendues sur un même écran, la dernière à s'initialiser écrasait la liste utilisée
 * par TOUTES les autres, y compris pour les calculs de gain/verges qui n'ont rien à voir
 * avec l'affichage. Elle est maintenant figée (Object.freeze) : aucune fonction de ce
 * fichier ne doit plus jamais la réassigner.
 */
export const DEFAULT_YARD_LINES: ReadonlyArray<string> = Object.freeze([SF, ...negativeLines, '55', ...positiveLines, TD]);

/**
 * Retourne la liste des yardLines à afficher dans une roulette (wheel-selector), selon le
 * contexte de l'écran (ex: 'inversed' pour une interception, 'simple' pour un punt).
 *
 * Fonction PURE : ne modifie aucun état partagé, un nouveau tableau est retourné à chaque
 * appel. Cette liste ne doit être utilisée que pour peupler l'affichage d'une roulette
 * (WheelSelectorComponent), jamais pour un calcul de jeu — voir DEFAULT_YARD_LINES.
 */
export function selectProperWheelYardLine(yardLinesToDisplay:string): Array<string> {
  switch (yardLinesToDisplay) {
    case 'inversed':
      return [TD, ...negativeLines, '55', ...positiveLines, SF];
    case 'simple':
      return [SF, ...negativeLines, '55', ...positiveLines, SIMPLE];
    case 'td-simple':
      return [TD, ...negativeLines, '55', ...positiveLines, SIMPLE];
    case 'default':
    case 'safety':
    default:
      return [...DEFAULT_YARD_LINES];
  }
}

/**
 *
 * @param from
 * @param to
 */
export function getDistance(from: string, to: string) {
  const yardIndexTo = getYardIndex(to);
  const yardIndexFrom = getYardIndex(from);
  if (yardIndexTo === -1) {
    throw new Error(`getDistance() : Index de to (${to}) non trouvé`)
  }
  if (yardIndexFrom === -1) {
    throw new Error(`getDistance() : Index de from (${from}) non trouvé`)
  }
  return yardIndexTo - yardIndexFrom
}

export function getYardIndex(yard: string) {
  // SIMPLE (rouge) n'est pas une position sur le terrain : elle occupe la
  // même place que TD dans DEFAULT_YARD_LINES (voir selectProperWheelYardLine
  // 'simple'/'td-simple', où SIMPLE remplace/complète TD en bout de liste).
  if (yard === SIMPLE) {
    return DEFAULT_YARD_LINES.indexOf(TD)
  }
  return [...DEFAULT_YARD_LINES, ...endZone].indexOf(yard)
}

/**
 *
 * @param yardLine
 * @param modificator
 */
export function calculateYardLine(yardLine: string, modificator: number) {
  const currentIndex = getYardIndex(yardLine)
  if (currentIndex === -1) {
    throw new Error(`calculateYardLine() : yardLine (${yardLine}) non trouvé`)
  }
  const index = currentIndex + modificator
  if (index < 0) {
    return DEFAULT_YARD_LINES[0]
  } else if (index >= DEFAULT_YARD_LINES.length) {
    return DEFAULT_YARD_LINES[DEFAULT_YARD_LINES.length - 1];
  }
  return DEFAULT_YARD_LINES[index];
}

export function toggleYardLine(yardLine: string): string {
  if ([SF, SIMPLE, '55', TD].includes(yardLine)) {
    return yardLine
  }

  const inverseYardLine = Number.parseInt(yardLine) * -1
  return inverseYardLine.toString()
}
